import React from "react";
import { Box, Typography, Grid, useMediaQuery } from "@mui/material";
import { Images } from "../../../utils/Images";

// Static fallback reasons (used when API returns no reasons)
const STATIC_REASONS = [
  {
    title: "Create Lasting Impact",
    description:
      "Whether you’re looking to upsize or downsize, we prioritize your unique goals.",
  },
  {
    title: "Enable Better Planning",
    description:
      "Consistent contributions allow us to plan ahead, ensuring resources and support are available exactly when they’re needed most.",
  },
  {
    title: "Make Giving Effortless",
    description:
      "With automatic monthly donations, you can create a big difference effortlessly while staying committed to the cause.",
  },
  {
    title: "See Your Impact Grow",
    description:
      "Watch your monthly support add up over time, creating meaningful change step by step.",
  },
];

// Reasons: use API reasons when present, otherwise fall back to static copy
const getReasonsFromData = (donationData) => {
  if (donationData?.reasons && Array.isArray(donationData.reasons) && donationData.reasons.length > 0) {
    return donationData.reasons;
  }
  return STATIC_REASONS;
};

const WhyDonateMonthly = ({ donationData }) => {
  const reasonsData = getReasonsFromData(donationData);
  const isMediumScreen = useMediaQuery(
    "(min-width: 900px) and (max-width: 1200px)"
  );
  const isSmallScreen = useMediaQuery("(max-width: 899px)");
  const isMobile = useMediaQuery("(max-width: 600px)");

  return (
    <Box
      sx={{
        width: "94.4%",
        mx: "auto",
        py: { xs: 4, md: "100px" },
        px: { xs: 1, md: 0 },
        backgroundColor: "#fff",
      }}
    >
      <Grid
        container
        sx={{
          mx: "auto",
          borderRadius: "32px",
          overflow: "hidden",
          background: "#fff",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          flexWrap: "nowrap",
          gap: "60px",
        }}
      >
        {/* Left Side: Reasons */}
        <Grid
          item
          xs={12}
          md={7}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            background: "#fff",
            flex: "1",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: isMobile ? "10px" :isSmallScreen?"13px": isMediumScreen?"13px":"13px",
                color: "#131313",
                fontWeight: 400,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                mb: 1,
              }}
            >
              <Box
                component="span"
                sx={{
                  color: "#A9C74E",
                  fontSize: "20px",
                  mr: 1,
                  ml: 1,
                  verticalAlign: "sub",
                }}
              >
                ●
              </Box>
              DONATE MONTHLY
            </Typography>
            <Typography
              sx={{
                fontSize:isMobile ? "18px" :isSmallScreen?"24px": isMediumScreen?"30px":  "36px" ,
                fontWeight: 700,
                color: "#222",
                mb:isMobile ? "28px" :isSmallScreen?"28px": isMediumScreen?"30px": "40px",
                lineHeight: 1.2,
              }}
            >
              Why Donate Every Month&nbsp;?
            </Typography>
          </Box>
          <Box sx={{ mt: 2, position: "relative" }}>
            <Box
              component="img"
              src={Images.dashLinev}
              alt="Target"
              sx={{
                width: "1px",
                height: "calc(100% - 100px)",
                position: "absolute",
                left: "20px",
                top: "17px",
              }}
            />
            {reasonsData.map((reason, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  position: "relative",
                  minHeight: "80px",
                  pb: idx !== reasonsData.length - 1 
  ? (isMobile ? "22px" : isSmallScreen ? "24px" : isMediumScreen ? "30px" : "32px")
  : 0,
                }}
              >
                {/* Icon and dashed line */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    mr: 3,
                    minWidth: "40px",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#9AB453",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="img"
                      src={Images.target}
                      alt="Target"
                      sx={{ width: "24px", height: "24px" }}
                    />
                  </Box>
                </Box>
                {/* Content */}
                <Box>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "14px" :isSmallScreen?"16px": isMediumScreen?"18px":"18px",
                      fontWeight: 600,
                      color: "#000",
                      mb: "8px",
                    }}
                  >
                    {reason.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: isMobile ? "12px" :isSmallScreen?"14px": isMediumScreen?"16px":"16px",
                      color: "#7D7D7D",
                      fontWeight: 400,
                      lineHeight: 1.6,
                    }}
                  >
                    {reason.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Grid>
        {/* Right Side: Image */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display:isMobile ? "none" :isSmallScreen?"none": isMediumScreen?"block": "block" ,
            flex: "1",
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              minHeight: "420px",
              maxHeight: "600px",
              objectFit: "cover",
              borderRadius: "32px",
              overflow: "hidden",
            }}
          >
            <Box
              component="img"
              src={donationData?.monthly_reason_image || ""}
              alt="Why Donate Monthly"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                borderTopRightRadius: "32px",
                borderBottomRightRadius: "32px",
                display: "block",
              }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WhyDonateMonthly;
